import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Wallet, Percent, Download, Trash2, Utensils,
} from "lucide-react";
import { FileUpload } from "@/components/FileUpload";
import { PnLChart } from "@/components/PnLChart";
import { CategoryTable, SourceSummary } from "@/components/SummaryTables";
import { TransactionsTable } from "@/components/TransactionsTable";
import {
  fmtAUD, totals, periodSeries, categoryBreakdown, sourceSummary,
  detectSources, filterBySource,
} from "@/lib/finance";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MetricCard = ({ label, value, sub, color, icon: Icon, testId }) => (
  <div className="rounded-sm border border-border bg-card p-4" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-neutral-500" />
    </div>
    <p className={`mt-3 tabular text-2xl font-semibold ${color || "text-white"}`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const Toggle = ({ options, value, onChange, testIdPrefix }) => (
  <div className="inline-flex rounded-sm border border-border bg-card p-0.5">
    {options.map((o) => (
      <button
        key={o.value}
        data-testid={`${testIdPrefix}-${o.value}`}
        onClick={() => onChange(o.value)}
        className={`rounded-sm px-3 h-7 text-xs font-medium transition-colors ${
          value === o.value ? "bg-white text-black" : "text-muted-foreground hover:text-white"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export default function Dashboard() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("monthly");
  const [source, setSource] = useState("all");
  const [meta, setMeta] = useState({ expense_categories: [], income_categories: [] });

  const loadAll = async () => {
    try {
      const [tRes, mRes] = await Promise.all([
        axios.get(`${API}/transactions`),
        axios.get(`${API}/meta`),
      ]);
      setTxns(tRes.data);
      setMeta(mRes.data);
    } catch (e) {
      toast.error("Could not load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleUpload = async (file) => {
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTxns(res.data);
      setSource("all");
      toast.success(`Imported ${res.data.length} transactions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (id, patch) => {
    setTxns((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try {
      await axios.patch(`${API}/transactions/${id}`, patch);
    } catch (e) {
      toast.error("Update failed");
      loadAll();
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear all imported transactions?")) return;
    try {
      await axios.delete(`${API}/transactions`);
      setTxns([]);
      setSource("all");
      toast.success("Cleared");
    } catch (e) {
      toast.error("Failed to clear");
    }
  };

  const handleExport = () => {
    window.open(`${API}/export`, "_blank");
  };

  const allSources = useMemo(() => detectSources(txns), [txns]);
  const filtered = useMemo(() => filterBySource(txns, source), [txns, source]);
  const t = useMemo(() => totals(filtered), [filtered]);
  const series = useMemo(() => periodSeries(filtered, mode), [filtered, mode]);
  const incomeCats = useMemo(() => categoryBreakdown(filtered, "income"), [filtered]);
  const expenseCats = useMemo(() => categoryBreakdown(filtered, "expense"), [filtered]);
  const srcSummary = useMemo(() => sourceSummary(txns), [txns]);

  const hasData = txns.length > 0;

  return (
    <div className="min-h-screen w-full bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-black/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 md:px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-white">
              <Utensils className="h-4 w-4 text-black" />
            </div>
            <div>
              <h1 className="text-sm font-semibold leading-none tracking-tight">Simply Indian</h1>
              <p className="mt-1 text-[11px] text-muted-foreground">Expense Tracker</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasData && (
              <>
                <button
                  data-testid="export-csv-btn"
                  onClick={handleExport}
                  className="inline-flex items-center gap-2 rounded-sm border border-border bg-secondary px-3 h-9 text-xs font-medium hover:bg-accent transition-colors"
                >
                  <Download className="h-3.5 w-3.5" /> Download CSV
                </button>
                <button
                  data-testid="clear-btn"
                  onClick={handleClear}
                  className="inline-flex items-center gap-2 rounded-sm border border-border px-3 h-9 text-xs font-medium text-muted-foreground hover:text-red-500 hover:border-red-500/40 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Clear
                </button>
              </>
            )}
            <FileUpload onUpload={handleUpload} uploading={uploading} compact />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-6 md:px-6">
        {loading ? (
          <div className="py-24 text-center text-sm text-muted-foreground">Loading…</div>
        ) : !hasData ? (
          <div className="fade-up mx-auto max-w-2xl py-10">
            <div className="mb-6">
              <h2 className="text-lg font-medium tracking-tight">Import a bank statement to begin</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Upload your CommBank export and we'll categorise income and expenses, split them by card and account, and build your weekly &amp; monthly P&amp;L.
              </p>
            </div>
            <FileUpload onUpload={handleUpload} uploading={uploading} />
          </div>
        ) : (
          <div className="fade-up space-y-6">
            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-1.5" data-testid="source-filter">
                <button
                  onClick={() => setSource("all")}
                  data-testid="source-tab-all"
                  className={`rounded-sm border px-3 h-8 text-xs font-medium transition-colors ${
                    source === "all" ? "border-white bg-white text-black" : "border-border bg-card text-muted-foreground hover:text-white"
                  }`}
                >
                  All
                </button>
                {allSources.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSource(s)}
                    data-testid={`source-tab-${s}`}
                    className={`rounded-sm border px-3 h-8 text-xs font-medium transition-colors ${
                      source === s ? "border-white bg-white text-black" : "border-border bg-card text-muted-foreground hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Toggle
                testIdPrefix="view-toggle"
                options={[{ label: "Weekly", value: "weekly" }, { label: "Monthly", value: "monthly" }]}
                value={mode}
                onChange={setMode}
              />
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <MetricCard testId="metric-income" label="Total income" value={fmtAUD(t.income)} color="text-green-500" icon={TrendingUp} />
              <MetricCard testId="metric-expenses" label="Total expenses" value={fmtAUD(t.expense)} color="text-red-500" icon={TrendingDown} />
              <MetricCard
                testId="metric-net"
                label="Net profit"
                value={`${t.net < 0 ? "−" : ""}${fmtAUD(Math.abs(t.net))}`}
                color={t.net >= 0 ? "text-green-500" : "text-red-500"}
                icon={Wallet}
              />
              <MetricCard
                testId="metric-margin"
                label="Net margin"
                value={`${t.margin.toFixed(1)}%`}
                color={t.margin >= 0 ? "text-green-500" : "text-red-500"}
                icon={Percent}
              />
            </div>

            {/* Chart + Source summary */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-sm border border-border bg-card p-4 lg:col-span-2">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-medium tracking-tight">
                    Income vs Expenses · Net profit ({mode === "weekly" ? "weekly" : "monthly"})
                  </h3>
                </div>
                <PnLChart data={series} mode={mode} />
              </div>
              <SourceSummary rows={srcSummary} />
            </div>

            {/* Category tables */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <CategoryTable testId="income-categories" title="Income by category" rows={incomeCats} color="#22c55e" />
              <CategoryTable testId="expense-categories" title="Expenses by category" rows={expenseCats} color="#ef4444" />
            </div>

            {/* Transactions */}
            <TransactionsTable
              txns={filtered}
              sources={allSources}
              expenseCats={meta.expense_categories}
              incomeCats={meta.income_categories}
              onUpdate={handleUpdate}
            />
          </div>
        )}
      </main>

      <footer className="border-t border-border py-5" data-testid="app-footer">
        <p className="text-center text-[11px] tracking-wide text-muted-foreground">
          Developed &amp; supported by <span className="font-semibold text-neutral-300">VecSpec</span>
        </p>
      </footer>
    </div>
  );
}
